/**
 * Key derivation: master key (Argon2id / PBKDF2) and HKDF sub-key derivation.
 *
 * The master password NEVER leaves the caller. These functions take a password
 * and return key material that the caller keeps in memory only.
 */
import { argon2id } from 'hash-wasm';
import { toBytes, type Bytes } from './encoding.js';
import type { KdfParams } from './types.js';

/** HKDF purpose labels — distinct labels guarantee distinct derived keys. */
export const INFO_ENC = 'battini/v1/encryption-key';
export const INFO_AUTH = 'battini/v1/authentication-hash';

/**
 * Derive the 32-byte master key from the master password and salt using the
 * user's chosen KDF parameters.
 */
export async function deriveMasterKey(
  password: string,
  salt: Bytes,
  params: KdfParams,
): Promise<Bytes> {
  if (params.algorithm === 'argon2id') {
    const hash = await argon2id({
      password,
      salt,
      parallelism: params.parallelism,
      memorySize: params.memoryKiB,
      iterations: params.iterations,
      hashLength: params.hashLengthBits / 8,
      outputType: 'binary',
    });
    return hash as Bytes;
  }

  // PBKDF2-SHA256 (native Web Crypto, no WASM) — compatibility fallback.
  const keyMaterial = await crypto.subtle.importKey('raw', toBytes(password), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: params.iterations, hash: params.hash },
    keyMaterial,
    params.hashLengthBits,
  );
  return new Uint8Array(bits);
}

/**
 * HKDF-SHA256 over the master key with a purpose `info` label.
 * Empty salt is valid per RFC 5869; the master key already carries the
 * password's entropy and the per-user salt.
 */
export async function hkdfSha256(
  masterKey: Bytes,
  info: string,
  lengthBytes = 32,
): Promise<Bytes> {
  const key = await crypto.subtle.importKey('raw', masterKey, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: toBytes(info) },
    key,
    lengthBytes * 8,
  );
  return new Uint8Array(bits);
}

/** Derive the encryption sub-key from the master key. */
export function deriveEncKey(masterKey: Bytes): Promise<Bytes> {
  return hkdfSha256(masterKey, INFO_ENC, 32);
}

/**
 * Derive the authentication hash from the master key. This is the ONLY
 * password-equivalent value ever sent to the server — it cannot decrypt the
 * vault (only encKey → symKey can).
 */
export function deriveAuthHash(masterKey: Bytes): Promise<Bytes> {
  return hkdfSha256(masterKey, INFO_AUTH, 32);
}

/**
 * Server-side verification hash of the client authHash.
 * PBKDF2-SHA256 with 100k iterations + per-user server salt. Runs on the Worker
 * via Web Crypto. Returns base64.
 */
export async function serverHashOfAuthHash(
  authHash: Bytes,
  serverSalt: Bytes,
  iterations = 100_000,
): Promise<Bytes> {
  const keyMaterial = await crypto.subtle.importKey('raw', authHash, 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: serverSalt, iterations, hash: 'SHA-256' },
    keyMaterial,
    256,
  );
  return new Uint8Array(bits);
}
